import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Search, Edit2, Trash2, Plus, Users, Package, Layers, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const money = (v) => (v === null || v === undefined ? '—' : `R$ ${Number(v).toFixed(2).replace('.', ',')}`);

// Aceita YYYY-MM-DD, ISO completo ou DD/MM/YYYY e devolve YYYY-MM-DD (para <input type="date">)
const toInputDate = (s) => {
  if (!s) return '';
  if (s.includes('/')) {
    const [d, m, y] = s.split(' ')[0].split('/');
    if (y) return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return s.split('T')[0];
};
const toDisplayDate = (s) => {
  const v = toInputDate(s);
  if (!v || !v.includes('-')) return s || '—';
  const [y, m, d] = v.split('-');
  return `${d}/${m}/${y}`;
};

const STATUS = {
  active: { label: 'Ativo', cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/20' },
  expiring: { label: 'Vencendo', cls: 'bg-amber-500/15 text-amber-500 border-amber-500/20' },
  expired: { label: 'Vencido', cls: 'bg-red-500/15 text-red-500 border-red-500/20' },
};

const emptyPlan = { name: '', price: '', duration_months: 1, description: '', active: true };

export default function VendedorClientesPage() {
  const { vendedorId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [vendedor, setVendedor] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [counts, setCounts] = useState({});
  const [revenue, setRevenue] = useState(0);
  const [plans, setPlans] = useState([]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [selected, setSelected] = useState([]);

  const [editCustomer, setEditCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ plan_id: 'keep', price_mode: 'keep', custom_price: '' });

  const [planDialog, setPlanDialog] = useState(null); // null | 'new' | plan
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [deletePlan, setDeletePlan] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [c, p] = await Promise.all([
        api.get(`/vendedores/${vendedorId}/customers`),
        api.get(`/vendedores/${vendedorId}/plans`),
      ]);
      setVendedor(c.data.vendedor);
      setCustomers(c.data.customers || []);
      setCounts(c.data.counts || {});
      setRevenue(c.data.monthly_revenue_estimate || 0);
      setPlans(p.data || []);
      setSelected([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar dados do vendedor');
      if (e.response?.status === 404 || e.response?.status === 403) navigate('/vendedores');
    } finally {
      setLoading(false);
    }
  }, [api, vendedorId, navigate]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return customers.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (planFilter !== 'all' && c.plan_id !== planFilter) return false;
      if (!q) return true;
      return (c.name || '').toLowerCase().includes(q)
        || (c.phone || '').includes(q)
        || (c.sigma_username || '').toLowerCase().includes(q);
    });
  }, [customers, search, statusFilter, planFilter]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selected.includes(c.id));
  const toggleAll = () => {
    if (allFilteredSelected) setSelected(selected.filter((id) => !filtered.some((c) => c.id === id)));
    else setSelected([...new Set([...selected, ...filtered.map((c) => c.id)])]);
  };

  // ---------- Cliente ----------
  const openCustomer = (c) => {
    setCustomerForm({
      name: c.name || '',
      phone: c.phone || '',
      plan_id: c.plan_id || '',
      use_custom: c.custom_price !== null && c.custom_price !== undefined,
      custom_price: c.custom_price ?? '',
      expiration_date: toInputDate(c.expiration_date),
      expiration_time: c.expiration_time || '09:00',
      notes: c.notes || '',
      payment_link_enabled: c.payment_link_enabled ?? true,
    });
    setEditCustomer(c);
  };

  const selectedPlanForCustomer = plans.find((p) => p.id === customerForm.plan_id);

  const saveCustomer = async () => {
    if (!editCustomer) return;
    if (customerForm.use_custom && (customerForm.custom_price === '' || Number(customerForm.custom_price) < 0)) {
      toast.error('Informe um preço personalizado válido');
      return;
    }
    const payload = {
      name: customerForm.name,
      phone: customerForm.phone,
      plan_id: customerForm.plan_id,
      custom_price: customerForm.use_custom ? parseFloat(customerForm.custom_price) : null,
      expiration_time: customerForm.expiration_time,
      notes: customerForm.notes,
      payment_link_enabled: customerForm.payment_link_enabled,
    };
    // Só envia a data se foi alterada (preserva o formato original do cliente)
    if (customerForm.expiration_date && customerForm.expiration_date !== toInputDate(editCustomer.expiration_date)) {
      payload.expiration_date = customerForm.expiration_date;
    }
    try {
      setSaving(true);
      await api.put(`/vendedores/${vendedorId}/customers/${editCustomer.id}`, payload);
      toast.success('Cliente atualizado');
      setEditCustomer(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar cliente');
    } finally { setSaving(false); }
  };

  const saveBulk = async () => {
    if (bulkForm.plan_id === 'keep' && bulkForm.price_mode === 'keep') {
      toast.error('Escolha um plano ou uma alteração de preço');
      return;
    }
    try {
      setSaving(true);
      const r = await api.post(`/vendedores/${vendedorId}/customers/bulk-update`, {
        customer_ids: selected,
        plan_id: bulkForm.plan_id === 'keep' ? null : bulkForm.plan_id,
        price_mode: bulkForm.price_mode,
        custom_price: bulkForm.price_mode === 'set' ? parseFloat(bulkForm.custom_price) : null,
      });
      toast.success(r.data.message);
      setBulkOpen(false);
      setBulkForm({ plan_id: 'keep', price_mode: 'keep', custom_price: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro na alteração em massa');
    } finally { setSaving(false); }
  };

  // ---------- Planos ----------
  const openPlan = (p) => {
    if (p === 'new') setPlanForm(emptyPlan);
    else setPlanForm({
      name: p.name, price: p.price, duration_months: p.duration_months || 1,
      description: p.description || '', active: p.active ?? true, sigma_package_id: p.sigma_package_id,
    });
    setPlanDialog(p);
  };

  const savePlan = async () => {
    if (!planForm.name || planForm.price === '' || Number(planForm.price) < 0) {
      toast.error('Preencha nome e preço');
      return;
    }
    const payload = {
      ...planForm,
      price: parseFloat(planForm.price),
      duration_months: parseInt(planForm.duration_months, 10) || 1,
    };
    try {
      setSaving(true);
      if (planDialog === 'new') await api.post(`/vendedores/${vendedorId}/plans`, payload);
      else await api.put(`/vendedores/${vendedorId}/plans/${planDialog.id}`, payload);
      toast.success(planDialog === 'new' ? 'Plano criado' : 'Plano atualizado');
      setPlanDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar plano');
    } finally { setSaving(false); }
  };

  const confirmDeletePlan = async () => {
    try {
      await api.delete(`/vendedores/${vendedorId}/plans/${deletePlan.id}`);
      toast.success('Plano excluído');
      setDeletePlan(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir plano');
    }
  };

  if (loading && !vendedor) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendedor-clientes-page">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigate('/vendedores')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Clientes de {vendedor?.username}</h1>
            <p className="text-muted-foreground text-sm">Veja e altere planos, preços e vencimentos da carteira deste vendedor</p>
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Atualizar
        </Button>
      </div>

      {/* Resumo */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Clientes</p>
          <p className="text-2xl font-bold">{customers.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Ativos / vencendo</p>
          <p className="text-2xl font-bold">
            <span className="text-emerald-500">{counts.active || 0}</span>
            <span className="text-muted-foreground text-lg"> / </span>
            <span className="text-amber-500">{counts.expiring || 0}</span>
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Vencidos</p>
          <p className="text-2xl font-bold text-red-500">{counts.expired || 0}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Receita mensal estimada</p>
          <p className="text-2xl font-bold text-blue-500">{money(revenue)}</p>
        </Card>
      </div>

      <Tabs defaultValue="clientes">
        <TabsList>
          <TabsTrigger value="clientes"><Users className="w-4 h-4 mr-1.5" /> Clientes</TabsTrigger>
          <TabsTrigger value="planos"><Package className="w-4 h-4 mr-1.5" /> Planos ({plans.length})</TabsTrigger>
        </TabsList>

        {/* ================= CLIENTES ================= */}
        <TabsContent value="clientes" className="space-y-3 mt-4">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Buscar por nome, telefone ou usuário Sigma..."
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="md:w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="expiring">Vencendo</SelectItem>
                <SelectItem value="expired">Vencidos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="md:w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os planos</SelectItem>
                {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between gap-2 px-1">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input type="checkbox" className="rounded" checked={allFilteredSelected} onChange={toggleAll} />
              Selecionar {filtered.length} exibido{filtered.length !== 1 ? 's' : ''}
            </label>
            {selected.length > 0 && (
              <Button size="sm" onClick={() => setBulkOpen(true)}>
                <Layers className="w-4 h-4 mr-1.5" /> Alterar {selected.length} selecionado{selected.length !== 1 ? 's' : ''}
              </Button>
            )}
          </div>

          {filtered.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground">
              {customers.length === 0 ? 'Este vendedor ainda não tem clientes' : 'Nenhum cliente encontrado com esses filtros'}
            </Card>
          ) : (
            <div className="space-y-2">
              {filtered.map((c) => {
                const st = STATUS[c.status] || STATUS.active;
                return (
                  <Card key={c.id} className="p-3">
                    <div className="flex items-center gap-3">
                      <input type="checkbox" className="rounded shrink-0" checked={selected.includes(c.id)}
                        onChange={(e) => setSelected(e.target.checked ? [...selected, c.id] : selected.filter((id) => id !== c.id))} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{c.name}</span>
                          <Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
                          <span>{c.phone}</span>
                          {c.sigma_username && <span>Sigma: {c.sigma_username}</span>}
                          <span>Vence {toDisplayDate(c.expiration_date)}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-medium">{c.plan_name || <span className="text-red-400">Sem plano</span>}</p>
                        <p className="text-xs">
                          {c.custom_price !== null && c.custom_price !== undefined ? (
                            <span className="text-blue-400" title={`Plano: ${money(c.plan_price)}`}>{money(c.custom_price)} personalizado</span>
                          ) : (
                            <span className="text-muted-foreground">{money(c.plan_price)}</span>
                          )}
                        </p>
                      </div>
                      <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => openCustomer(c)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ================= PLANOS ================= */}
        <TabsContent value="planos" className="space-y-3 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Mudar o preço de um plano muda o valor de todos os clientes dele que não têm preço personalizado.
            </p>
            <Button size="sm" onClick={() => openPlan('new')}><Plus className="w-4 h-4 mr-1.5" /> Novo plano</Button>
          </div>
          {plans.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground">Este vendedor não tem planos</Card>
          ) : plans.map((p) => (
            <Card key={p.id} className={`p-3 ${!p.active ? 'opacity-60' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{p.name}</span>
                    {!p.active && <Badge variant="outline" className="text-[10px]">Inativo</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {p.duration_months || 1} {(p.duration_months || 1) > 1 ? 'meses' : 'mês'} · {p.customers_count || 0} cliente{p.customers_count !== 1 ? 's' : ''}
                  </p>
                </div>
                <span className="text-lg font-semibold">{money(p.price)}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => openPlan(p)}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8 border-red-500/30 text-red-500 hover:bg-red-500/10"
                  onClick={() => setDeletePlan(p)} disabled={(p.customers_count || 0) > 0}
                  title={(p.customers_count || 0) > 0 ? 'Há clientes neste plano' : 'Excluir'}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Editar cliente */}
      <Dialog open={!!editCustomer} onOpenChange={(o) => !o && setEditCustomer(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar {editCustomer?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nome</Label>
                <Input value={customerForm.name || ''} onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Telefone</Label>
                <Input value={customerForm.phone || ''} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Plano</Label>
              <Select value={customerForm.plan_id || ''} onValueChange={(v) => setCustomerForm({ ...customerForm, plan_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione um plano" /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} — {money(p.price)}{!p.active ? ' (inativo)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg border border-border/50 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs">Preço personalizado</Label>
                  <p className="text-[10px] text-muted-foreground">
                    Desligado = usa o preço do plano ({money(selectedPlanForCustomer?.price)})
                  </p>
                </div>
                <Switch checked={!!customerForm.use_custom}
                  onCheckedChange={(v) => setCustomerForm({ ...customerForm, use_custom: v })} />
              </div>
              {customerForm.use_custom && (
                <Input type="number" min="0" step="0.01" placeholder="0,00" value={customerForm.custom_price}
                  onChange={(e) => setCustomerForm({ ...customerForm, custom_price: e.target.value })} />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Vencimento</Label>
                <Input type="date" value={customerForm.expiration_date || ''}
                  onChange={(e) => setCustomerForm({ ...customerForm, expiration_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Horário</Label>
                <Input type="time" value={customerForm.expiration_time || ''}
                  onChange={(e) => setCustomerForm({ ...customerForm, expiration_time: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Observações</Label>
              <Input value={customerForm.notes || ''} onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Link de pagamento</Label>
              <Switch checked={!!customerForm.payment_link_enabled}
                onCheckedChange={(v) => setCustomerForm({ ...customerForm, payment_link_enabled: v })} />
            </div>
            <Button className="w-full" onClick={saveCustomer} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar alterações'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alteração em massa */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Alterar {selected.length} cliente{selected.length !== 1 ? 's' : ''}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Plano</Label>
              <Select value={bulkForm.plan_id} onValueChange={(v) => setBulkForm({ ...bulkForm, plan_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Manter o plano atual</SelectItem>
                  {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {money(p.price)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Preço</Label>
              <Select value={bulkForm.price_mode} onValueChange={(v) => setBulkForm({ ...bulkForm, price_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Manter como está</SelectItem>
                  <SelectItem value="clear">Usar o preço do plano (remove personalizado)</SelectItem>
                  <SelectItem value="set">Definir preço personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {bulkForm.price_mode === 'set' && (
              <div>
                <Label className="text-xs">Novo preço (R$)</Label>
                <Input type="number" min="0" step="0.01" value={bulkForm.custom_price}
                  onChange={(e) => setBulkForm({ ...bulkForm, custom_price: e.target.value })} />
              </div>
            )}
            <Button className="w-full" onClick={saveBulk} disabled={saving}>
              {saving ? 'Aplicando...' : 'Aplicar alteração'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Criar/editar plano */}
      <Dialog open={!!planDialog} onOpenChange={(o) => !o && setPlanDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{planDialog === 'new' ? `Novo plano para ${vendedor?.username}` : `Editar ${planDialog?.name}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Nome</Label>
              <Input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} placeholder="Mensal" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Preço (R$)</Label>
                <Input type="number" min="0" step="0.01" value={planForm.price}
                  onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Duração (meses)</Label>
                <Input type="number" min="1" step="1" value={planForm.duration_months}
                  onChange={(e) => setPlanForm({ ...planForm, duration_months: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Descrição</Label>
              <Input value={planForm.description || ''} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Ativo</Label>
              <Switch checked={!!planForm.active} onCheckedChange={(v) => setPlanForm({ ...planForm, active: v })} />
            </div>
            {planDialog !== 'new' && planDialog?.customers_count > 0 && (
              <p className="text-xs text-amber-500">
                {planDialog.customers_count} cliente(s) usam este plano. Os que não têm preço personalizado passarão a pagar o novo valor.
              </p>
            )}
            <Button className="w-full" onClick={savePlan} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar plano'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Excluir plano */}
      <Dialog open={!!deletePlan} onOpenChange={(o) => !o && setDeletePlan(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="text-red-400">Excluir plano {deletePlan?.name}?</DialogTitle></DialogHeader>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => setDeletePlan(null)}>Cancelar</Button>
            <Button variant="destructive" className="flex-1" onClick={confirmDeletePlan}>Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
